import { Component, Vue, Watch } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import {
	Configuration,
	FrontendApi,
	LoginFlow,
	UiNodeInputAttributes,
	UiText,
	UpdateLoginFlowWithPasswordMethod
} from '@ory/client';
import { AxiosError } from 'axios';
import auth from '@/store/modules/auth';
import { router, validation, ErrorFrontend, ErrorBackend } from '@/_helpers';
import template from './Login.vue';
import Button from '@/components/Button';
import { env } from '@/_helpers/env';

import MDTextFieldWithValidation from '@/components/Validation/MDTextFieldWithValidation';
import { feApi } from '@/_helpers/ory';

@Component({
	mixins: [template],
	components: {
		ValidationObserver,
		MDTextFieldWithValidation,
		Button
	}
})
export default class Login extends Vue {
	dashboardUrl = env.DASHBOARD_DOMAIN;

	form: UpdateLoginFlowWithPasswordMethod = {
		csrf_token: '',
		identifier: '',
		password: '',
		method: ''
	};

	alertShow: boolean = false;

	AlertTxtIdx: number = 0;

	alertTxt: string[] = [
		`Je email is nog niet geverifiëerd, klik <a href="${env.WEBSITE_DOMAIN}/verify?email=${this.form.identifier}">hier</a> en verifieer je account.`,
		'Je proefperiode is afgelopen, neem contact op met contact@qrlink.nl om je account te activeren.',
		'Je abonnement is stopgezet, neem contact op met contact@qrlink.nl om je abonnement te heractiveren.'
	];

	loading: boolean = true;

	loggedIn: boolean = false;

	get isLoggedIn(): boolean {
		return auth.isLoggedIn || this.loggedIn;
	}

	flow: LoginFlow | null = null;

	async mounted(): Promise<void> {
		await this.getFlow();

		if (this.isLoggedIn) {
			this.continue();
		}

		this.loading = false;
	}

	@Watch('form', { deep: true })
	formChange(): void {
		(this.$refs.form as any).reset();
	}

	async submit() {
		if (this.loading) return;

		const isValid = await (this.$refs.form as any).validate();
		if (!isValid) {
			return;
		}

		if (!this.flow) {
			return;
		}

		this.loading = true;
		this.alertShow = false;

		feApi
			.updateLoginFlow({ flow: this.flow.id, updateLoginFlowBody: this.form })
			.then(() => {
				this.continue();
			})
			.catch(async (error: AxiosError) => {
				switch (error.response?.status) {
					case 400:
						this.setFlow(error.response.data);
						break;
					case 410:
						await this.getFlow();
						this.submit();
						break;
					default:
				}
			})
			.finally(() => {
				this.loading = true;
			});
	}

	async getFlow() {
		const flowID: string = this.$route.query.flow as string;
		let getNewFlowID: boolean = flowID === '' || flowID === undefined;

		if (!getNewFlowID) {
			feApi
				.getLoginFlow({ id: flowID })
				.then((response) => {
					this.setFlow(response.data);
				})
				.catch((error: AxiosError) => {
					switch (error.response?.status) {
						case 400:
							// Session already available, redirect to dashboard
							this.loggedIn = true;
							break;
						case 410:
							getNewFlowID = true;
							break;
						default:
							getNewFlowID = true;
					}
				});
		}

		if (getNewFlowID) {
			feApi
				.createBrowserLoginFlow()
				.then((response) => {
					this.setFlow(response.data);

					// ToDo figure out if we can do this without a refresh
					router.replace({ name: 'login', query: { flow: this.flow?.id }, replace: true });
				})
				.catch((error: AxiosError) => {
					switch (error.response?.status) {
						case 400:
							// Session already available, redirect to dashboard
							this.loggedIn = true;
							break;
						default:
					}
				});
		}
	}

	setFlow(flow: LoginFlow) {
		flow.ui.nodes.forEach((value, index, array) => {
			const node: UiNodeInputAttributes = value.attributes as UiNodeInputAttributes;
			switch (node.name) {
				case 'csrf_token':
					this.form.csrf_token = node.value;
					break;
				case 'method':
					this.form.method = node.value;
					break;
				default:
					break;
			}
		});

		if (flow.return_to) {
			router.replace({ query: { return_to: flow.return_to } }); // ToDo figure out if we can do this without a refresh
		}

		if (flow.ui.messages) {
			this.setFlowMessage(flow.ui.messages);
		}

		this.flow = flow;
	}

	setFlowMessage(msgs: UiText[]): void {
		if (msgs.length > 0) {
			if (msgs[0].type === 'error') {
				switch (msgs[0].id) {
					case 4000006:
						(this.$refs.form as any).setErrors(
							this.customErrorAdder(validation.getErrors(this.getIncorrectEmailPasswordErrors()))
						);
						break;
					case 4000010:
						this.AlertTxtIdx = 0;
						this.alertShow = true;
						break;
					default:
						break;
				}
			}
		}
	}

	continue() {
		if (this.flow !== null && this.flow.return_to) {
			// eslint-disable-next-line no-restricted-globals
			location.href = this.flow.return_to;
		} else {
			router.push({ name: 'overview' });
		}
	}

	getIncorrectEmailPasswordErrors(): ErrorBackend[] {
		return [
			{
				code: 'incorrect_email_password',
				title: 'E-mailadres/wachtwoord incorrect',
				detail: 'Het e-mailadres of wachtwoord is incorrect.',
				source: {
					pointer: 'password'
				},
				metadata: {
					min: 0,
					max: 0,
					length: 0,
					in: []
				}
			}
		];
	}

	customErrorAdder(errors: ErrorFrontend): ErrorFrontend {
		const returnErrors = errors;
		if (returnErrors.password !== null) {
			returnErrors.email = [''];
		}
		return returnErrors;
	}
}
